"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnPhysicalPartResolver = void 0;
exports.resolveEmptyDesignatorPhysicalPartBijection = resolveEmptyDesignatorPhysicalPartBijection;
exports.getPhysicalPartComponentName = getPhysicalPartComponentName;
exports.findUnificationConflict = findUnificationConflict;
exports.findNumberingStyleDisagreement = findNumberingStyleDisagreement;
const OrcadDsnUtils_1 = require("./OrcadDsnUtils");
const OrcadDsnSectionNumbering_1 = require("./OrcadDsnSectionNumbering");
function resolveEmptyDesignatorPhysicalPartBijection(placements, physicalParts) {
    const result = new Map();
    const empty = placements.filter((placement) => placement.deviceDesignator === '');
    if (empty.length < 2) {
        return result;
    }
    if (empty.length !== physicalParts.length) {
        for (const placement of empty) {
            result.set(placement.key, {
                kind: 'declined',
                message: `[empty-designator-physical-part-declined] placements=${empty.length} sections=${physicalParts.length} reason=incomplete-bijection`,
            });
        }
        return result;
    }
    const maps = physicalParts.map((part) => part.PinNumber ?? []);
    const portSurface = empty[0]?.portSurface;
    if (!portSurface ||
        empty.some((placement) => placement.portSurface !== portSurface) ||
        !haveIdenticalOrdinalSurfaces(maps) ||
        !arePairwiseDisjointPhysicalParts(maps)) {
        for (const placement of empty) {
            result.set(placement.key, {
                kind: 'declined',
                message: `[empty-designator-physical-part-declined] placements=${empty.length} sections=${physicalParts.length} reason=non-isomorphic-or-overlapping-pin-maps`,
            });
        }
        return result;
    }
    const byDbId = [...empty].sort((left, right) => left.dbId - right.dbId || left.key.localeCompare(right.key));
    const byPage = [...empty].sort((left, right) => left.pageOrdinal - right.pageOrdinal || left.dbId - right.dbId || left.key.localeCompare(right.key));
    if (byDbId.some((placement, index) => placement.key !== byPage[index]?.key)) {
        for (const placement of empty) {
            result.set(placement.key, {
                kind: 'declined',
                message: `[empty-designator-physical-part-declined] placements=${empty.length} sections=${physicalParts.length} reason=order-key-disagreement globalDbId=${byDbId.map((item) => item.dbId).join(',')} pageMajor=${byPage.map((item) => `${item.pageOrdinal}:${item.dbId}`).join(',')}`,
            });
        }
        return result;
    }
    for (const [physicalPartIndex, placement] of byDbId.entries()) {
        result.set(placement.key, {
            kind: 'inferred',
            physicalPartIndex,
            message: `[empty-designator-physical-part-inferred] placement=${placement.key} physicalPartIndex=${physicalPartIndex + 1} order=global-dbId/page-major`,
        });
    }
    return result;
}
function haveIdenticalOrdinalSurfaces(maps) {
    const surface = maps[0]?.map((pin) => pin.Position);
    return (!!surface &&
        maps.every((map) => map.length === surface.length && map.every((pin, index) => pin.Position === surface[index])));
}
function arePairwiseDisjointPhysicalParts(maps) {
    for (let left = 0; left < maps.length; left++) {
        const seen = new Set(maps[left].map((pin) => String(pin.Number)));
        for (let right = left + 1; right < maps.length; right++) {
            if (maps[right].some((pin) => seen.has(String(pin.Number)))) {
                return false;
            }
        }
    }
    return true;
}
const AMBIGUOUS_OCCURRENCE_REFERENCE = Symbol('ambiguous-occurrence-reference');
const AMBIGUOUS_KEY_PREFIX = '\0orcad-ambiguous-occurrence-reference\0';
class OrcadDsnPhysicalPartResolver {
    constructor() {
        this.indexCache = new Map();
        this.occurrenceReferenceCache = new Map();
    }
    getPhysicalPartReference(design, partInst) {
        const dbId = partInst.DbId.toString();
        const occurrenceReference = this.getOccurrenceReferenceMap(design).get(dbId);
        if (occurrenceReference === AMBIGUOUS_OCCURRENCE_REFERENCE) {
            return `${AMBIGUOUS_KEY_PREFIX}${dbId}`;
        }
        return occurrenceReference ?? partInst.Reference;
    }
    getIndexForDesign(design) {
        const cached = this.indexCache.get(design);
        if (cached) {
            return cached;
        }
        const index = this.buildPhysicalPartIndex(design);
        this.indexCache.set(design, index);
        return index;
    }
    getOccurrenceReferenceMap(design) {
        const cached = this.occurrenceReferenceCache.get(design);
        if (cached) {
            return cached;
        }
        const referencesByDbId = new Map();
        const visit = (nodes) => {
            for (const node of nodes ?? []) {
                const partReference = node.UserProperty?.find((property) => property.Name === 'Part Reference')?.Value;
                if (node.RefInstId && partReference !== undefined) {
                    const existing = referencesByDbId.get(node.RefInstId);
                    if (existing !== undefined && existing !== partReference) {
                        referencesByDbId.set(node.RefInstId, AMBIGUOUS_OCCURRENCE_REFERENCE);
                    }
                    else {
                        referencesByDbId.set(node.RefInstId, partReference);
                    }
                }
                visit(node.InstOccurrence);
            }
        };
        visit(design.Occurrences?.InstOccurrence);
        this.occurrenceReferenceCache.set(design, referencesByDbId);
        return referencesByDbId;
    }
    buildPhysicalPartIndex(design) {
        const sectionsByReference = new Map();
        for (const schematic of design.Schematic ?? []) {
            for (const page of schematic.Page ?? []) {
                for (const partInst of page.PartInst ?? []) {
                    if (partInst.ContentsViewType === 1 && partInst.ContentsLibName) {
                        continue;
                    }
                    const reference = this.getPhysicalPartReference(design, partInst);
                    if (!reference || reference.includes('?')) {
                        continue;
                    }
                    const sectionKey = `${partInst.LibName}\0${partInst.PkgName}`;
                    let sections = sectionsByReference.get(reference);
                    if (!sections) {
                        sections = new Map();
                        sectionsByReference.set(reference, sections);
                    }
                    if (!sections.has(sectionKey)) {
                        sections.set(sectionKey, {
                            libName: partInst.LibName,
                            packageName: partInst.PkgName,
                            representativePartInst: partInst,
                        });
                    }
                }
            }
        }
        const index = { parts: new Map(), conflicts: [] };
        for (const [reference, sectionMap] of sectionsByReference) {
            if (sectionMap.size < 2) {
                continue;
            }
            const sections = Array.from(sectionMap.values());
            const conflict = findUnificationConflict(design.Cache, reference, sections);
            if (conflict) {
                index.conflicts.push(conflict);
                continue;
            }
            index.parts.set(reference, {
                reference,
                sections,
                componentName: getPhysicalPartComponentName(sections),
            });
        }
        return index;
    }
}
exports.OrcadDsnPhysicalPartResolver = OrcadDsnPhysicalPartResolver;
function getPhysicalPartComponentName(sections) {
    const sameLib = sections.every((s) => s.libName === sections[0].libName);
    const names = sections.map((s) => (sameLib ? s.packageName : `${s.libName}:${s.packageName}`)).sort();
    const prefix = longestCommonPrefix(names);
    const grouped = `${prefix}{${names.map((n) => n.slice(prefix.length)).join(',')}}`;
    return sameLib ? `${sections[0].libName}:${grouped}` : grouped;
}
function longestCommonPrefix(sortedNames) {
    const first = sortedNames[0];
    const last = sortedNames[sortedNames.length - 1];
    let length = 0;
    while (length < first.length && length < last.length && first[length] === last[length]) {
        length++;
    }
    return first.slice(0, length);
}
function findUnificationConflict(cache, reference, sections) {
    const symbolRefOwners = new Map();
    const pinNumbersBySection = [];
    for (const section of sections) {
        const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, section.representativePartInst);
        if (!resolution) {
            return {
                reference,
                message: `Package ${section.libName}:${section.packageName} for reference ${reference} not found in design cache`,
            };
        }
        const sectionName = `${section.libName}:${section.packageName}`;
        pinNumbersBySection.push({ sectionName, ...collectSectionPinNumbers(resolution.pkg) });
        for (const symbolRef of enumerateSymbolRefs(resolution.pkg)) {
            const owner = symbolRefOwners.get(symbolRef);
            if (owner !== undefined && owner !== sectionName) {
                return {
                    reference,
                    message: `Sections ${owner} and ${sectionName} of reference ${reference} both define symbol ${symbolRef}`,
                };
            }
            symbolRefOwners.set(symbolRef, sectionName);
        }
    }
    for (const section of pinNumbersBySection) {
        for (const duplicated of section.duplicated) {
            const other = pinNumbersBySection.find((s) => s !== section && s.numbers.has(duplicated));
            if (other) {
                return {
                    reference,
                    message: `Pin number ${duplicated} is assigned to more than one pin in section ${section.sectionName} and also appears in section ${other.sectionName} of reference ${reference}, so pins cannot be safely matched across packages`,
                };
            }
        }
    }
    return undefined;
}
function findNumberingStyleDisagreement(cache, sections) {
    let chosen;
    for (const section of sections) {
        const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, section.representativePartInst);
        if (!resolution) {
            continue;
        }
        const style = (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(resolution.pkg.AlphabeticNumbering);
        const sectionName = `${section.libName}:${section.packageName}`;
        if (!chosen) {
            chosen = { style, sectionName };
        }
        else if (style !== chosen.style) {
            return {
                chosenStyle: chosen.style,
                chosenSectionName: chosen.sectionName,
                dissentingStyle: style,
                dissentingSectionName: sectionName,
            };
        }
    }
    return undefined;
}
function collectSectionPinNumbers(pkg) {
    const numbers = new Set();
    const duplicated = new Set();
    for (const libPart of pkg.LibPart ?? []) {
        for (const physicalPart of libPart.PhysicalPart ?? []) {
            const seen = new Set();
            for (const pinNumber of physicalPart.PinNumber ?? []) {
                const number = pinNumber.Number.toString();
                if (seen.has(number)) {
                    duplicated.add(number);
                }
                seen.add(number);
                numbers.add(number);
            }
        }
    }
    return { numbers, duplicated };
}
function enumerateSymbolRefs(pkg) {
    const refs = [];
    for (const libPart of pkg.LibPart ?? []) {
        for (const normalView of libPart.NormalView ?? []) {
            const physicalParts = libPart.PhysicalPart ?? [];
            if (physicalParts.length > 1) {
                for (let physIdx = 0; physIdx < physicalParts.length; physIdx++) {
                    refs.push(`${libPart.CellName}${normalView.Suffix}-${physIdx + 1}`);
                }
            }
            else {
                refs.push(`${libPart.CellName}${normalView.Suffix}`);
            }
        }
    }
    return refs;
}
//# sourceMappingURL=OrcadDsnPhysicalParts.js.map